import { usePersonnel } from 'features/Responder/features/Personnel';
import React, { useEffect, useMemo } from 'react';
import { Alert, Form, Spinner } from 'react-bootstrap';
import { useSchedules } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import ScheduleCard from './components/ScheduleCard';

const ScheduleList = ({ type = 'schedule' }) => {
  const { schedules, fetchSchedules, lastUpdateTime, colorMap } = useSchedules();
  const { personnel: ctxPersonnel } = usePersonnel();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');

  const personnel = Array.isArray(ctxPersonnel) ? ctxPersonnel : Object.values(ctxPersonnel || {});

  useEffect(() => {
    setLoading(true);
    fetchSchedules()
      .catch(() => setError('Failed to load schedules'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [fetchSchedules]);

  const filteredSchedules = useMemo(() => {
    if (!schedules || !searchQuery.trim()) return schedules;

    const query = searchQuery.toLowerCase().trim();
    return schedules.filter((sch) => {
      // Search in schedule name
      if (sch.name?.toLowerCase().includes(query)) return true;

      // Search in notes
      if (sch.notes?.toLowerCase().includes(query)) return true;

      // Search in tour/zone name
      if (sch.entity?.type === 'Tour' && sch.entity?.name?.toLowerCase().includes(query))
        return true;
      if (sch.entity?.type === 'Zone' && sch.entity?.name?.toLowerCase().includes(query))
        return true;

      // Search in personnel names using personnel context
      if (Array.isArray(sch.personnel_ids) && sch.personnel_ids.length > 0) {
        const personnelMatch = sch.personnel_ids.some((personnelId) => {
          const person = personnel.find((p) => (p.userId ?? p.id) === personnelId);
          if (!person) return false;

          const searchableText = [person.name, person.fullName, person.username, person.email]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return searchableText.includes(query);
        });
        if (personnelMatch) return true;
      }

      return false;
    });
  }, [schedules, searchQuery, personnel]);

  return (
    <div
      className="p-3 rounded-2 d-flex h-100 flex-column"
      style={{ background: '#0b1826', height: '100%', minHeight: 0 }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="text-white mb-0">Schedules</h4>
      </div>
      <div className="mb-3">
        <Form.Control
          type="text"
          placeholder="Search schedules by name, notes, tour, or personnel..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-dark text-white border-secondary"
        />
      </div>
      <div className="overflow-auto flex-grow-1" style={{ minHeight: 0 }}>
        {loading && <Spinner animation="border" variant="light" className="my-4" />}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && (!filteredSchedules || filteredSchedules.length === 0) && (
          <div className="text-white-50">
            {searchQuery ? 'No schedules match your search.' : 'No schedules found.'}
          </div>
        )}
        {filteredSchedules && filteredSchedules.length > 0 && (
          <div className="d-flex flex-column gap-3">
            {filteredSchedules.map((sch, idx) => (
              <ScheduleCard sch={sch} idx={idx} key={sch.id || idx} type={type} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleList;
